<template>
  <div class="map-shell">
    <gas-page-shell :breadcrumb="['首页', '地图']">
      <div v-loading="loading" class="map-page">
        <div class="map-main">
          <div ref="mapContainer" class="map-chart"></div>
          <div class="map-mode-switch">
            <button
              v-for="mode in mapModes"
              :key="mode.value"
              type="button"
              :class="{ active: mapMode === mode.value }"
              @click="setMapMode(mode.value)"
            >
              {{ mode.label }}
            </button>
          </div>
          <div v-if="approxCount > 0" class="map-hint">
            {{ approxCount }} 台设备未填经纬度，落点为区域估算；在「设备管理」填写经纬度后可定位到街道/小区。
          </div>
        </div>
        <aside class="map-sidebar">
          <div class="sidebar-head">
            <span class="sidebar-title">设备列表</span>
            <div class="sidebar-stats">
              <span class="st online"><i class="dot"></i>{{ stats.online }}</span>
              <span class="st alarm"><i class="dot"></i>{{ stats.alarm }}</span>
              <span class="st offline"><i class="dot"></i>{{ stats.offline }}</span>
            </div>
          </div>
          <div class="sidebar-search">
            <el-input
              v-model="keyword"
              clearable
              size="small"
              placeholder="搜索设备名称/编号/地址"
              prefix-icon="el-icon-search"
              @keyup.enter.native="applyFilter"
              @clear="applyFilter"
            />
            <el-button size="small" icon="el-icon-refresh" circle title="刷新" @click="loadDashboard" />
            <el-button size="small" icon="el-icon-full-screen" circle title="适配全部设备" @click="fitAllDevices" />
          </div>
          <div class="device-scroll">
            <div
              v-for="item in filteredDevices"
              :key="item.deviceId"
              class="device-card"
              :class="[item.mapStatus, { active: activeId === item.deviceId }]"
              @click="focusDevice(item)"
            >
              <div class="card-top">
                <span class="status-tag" :class="item.mapStatus">{{ statusLabel(item.mapStatus) }}</span>
                <i class="el-icon-location-outline card-loc" :class="{ precise: item._preciseCoord }" :title="item._preciseCoord ? '精确坐标' : '区域估算'"></i>
              </div>
              <div class="card-name">{{ item.deviceName || '未命名设备' }}</div>
              <div class="card-code">{{ item.deviceCode || '—' }}</div>
              <div class="card-site" :title="item.siteName || ''">{{ item.siteName || '—' }}</div>
              <div v-if="item.mapStatus === 'alarm' && item.alarmMessage" class="card-alarm-msg">
                {{ item.alarmMessage }}
              </div>
            </div>
            <div v-if="!filteredDevices.length" class="empty-tip">暂无匹配设备</div>
          </div>
          <div class="sidebar-foot" @click="goDeviceList">
            查看全部 {{ stats.total }} 台设备 <i class="el-icon-arrow-right"></i>
          </div>
        </aside>
      </div>
    </gas-page-shell>
  </div>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import { gasApi } from '@/api/gas'
import { getDeviceTypeMetrics, formatMonitorMetricValue } from '@/config/deviceTypeMetrics'
import { resolveDeviceCoord, hasPreciseCoord } from '@/utils/mapCityCoords'
import {
  createDeviceMap,
  renderDeviceMarkers,
  fitMapToMarkers,
  focusMarker,
  setMapToChinaView,
  setMapBaseLayer,
  MAP_BASE_MODES
} from '@/utils/leafletMap'

export default {
  components: { GasPageShell },
  data() {
    return {
      loading: false,
      keyword: '',
      filterKeyword: '',
      devices: [],
      stats: { total: 0, online: 0, offline: 0, alarm: 0 },
      activeId: null,
      map: null,
      mapMode: MAP_BASE_MODES.street,
      mapModes: [
        { label: '标准', value: MAP_BASE_MODES.street },
        { label: '卫星', value: MAP_BASE_MODES.satellite }
      ],
      markerLayer: null,
      markerById: new Map(),
      markers: []
    }
  },
  computed: {
    filteredDevices() {
      const kw = (this.filterKeyword || '').trim().toLowerCase()
      if (!kw) return this.devices
      return this.devices.filter(d => {
        const name = (d.deviceName || '').toLowerCase()
        const code = (d.deviceCode || '').toLowerCase()
        const site = (d.siteName || '').toLowerCase()
        return name.includes(kw) || code.includes(kw) || site.includes(kw)
      })
    },
    approxCount() {
      return this.devices.filter(d => !d._preciseCoord).length
    }
  },
  mounted() {
    this.initMap()
    this.loadDashboard()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    statusLabel(s) {
      return { online: '在线', offline: '离线', alarm: '告警' }[s] || s
    },
    applyFilter() {
      this.filterKeyword = this.keyword
    },
    normalizeDevices(list) {
      return (list || []).map(d => {
        const row = {
          ...d,
          _preciseCoord: hasPreciseCoord(d)
        }
        row._popupMetrics = this.mapPopupMetrics(row)
        row._popupSubDevices = this.mapPopupSubDevices(row)
        return row
      })
    },
    mapPopupSubDevices(device) {
      const list = (device && device.subDevices) || []
      if (list.length <= 1) return []
      return list.map(sub => ({
        code: sub.code,
        label: sub.label || sub.code,
        metrics: this.mapPopupMetrics(device, Object.assign({}, device, sub.metrics || {}))
      }))
    },
    mapPopupMetrics(device, source) {
      const row = source || device
      const type = device && (device.deviceType || device.terminalType)
      return getDeviceTypeMetrics(type).map(metric => ({
        key: metric.key,
        label: metric.label,
        unit: metric.unit,
        value: formatMonitorMetricValue(this.metricValue(row, metric), metric)
      }))
    },
    metricValue(row, metric) {
      if (!row || !metric) return null
      if (metric.key === 'outletPressure') {
        return row.outletPressure != null ? row.outletPressure : row.pressure
      }
      if (metric.key === 'leakage') {
        if (row.leakage != null) return row.leakage
        if (row.inletLeak != null) return row.inletLeak
        return row.concentration
      }
      return row[metric.key]
    },
    formatMetricValue(value) {
      if (value == null || value === '') return '-'
      const numeric = Number(value)
      if (Number.isFinite(numeric)) {
        return numeric.toLocaleString(undefined, { maximumFractionDigits: 4 })
      }
      return String(value)
    },
    initMap() {
      const el = this.$refs.mapContainer
      if (!el || this.map) return
      this.map = createDeviceMap(el, { baseMode: this.mapMode })
    },
    setMapMode(mode) {
      if (!this.map) return
      this.mapMode = mode
      setMapBaseLayer(this.map, mode)
    },
    async loadDashboard() {
      this.loading = true
      try {
        const res = await gasApi.mapDashboard()
        const data = res.data || {}
        this.stats = { total: 0, online: 0, offline: 0, alarm: 0, ...(data.stats || {}) }
        this.devices = this.normalizeDevices(data.devices)
        await this.$nextTick()
        this.initMap()
        this.renderMarkers()
      } finally {
        this.loading = false
      }
    },
    renderMarkers() {
      if (!this.map) return
      if (this.markerLayer) {
        this.map.removeLayer(this.markerLayer)
        this.markerLayer = null
      }
      const { layer, markers, byId } = renderDeviceMarkers(this.map, this.devices, {
        coordResolver: resolveDeviceCoord,
        statusLabel: this.statusLabel,
        onMarkerHover: item => {
          this.activeId = item.deviceId
        },
        onMarkerClick: item => {
          this.activeId = item.deviceId
        }
      })
      this.markerLayer = layer
      this.markers = markers
      this.markerById = byId
      this.$nextTick(() => {
        setMapToChinaView(this.map)
      })
    },
    focusDevice(item) {
      this.activeId = item.deviceId
      const marker = this.markerById.get(item.deviceId)
      if (!marker || !this.map) return
      const zoom = item._preciseCoord ? 18 : 17
      focusMarker(this.map, marker, zoom)
    },
    fitAllDevices() {
      if (this.map && this.markers.length) {
        fitMapToMarkers(this.map, this.markers, { maxZoom: 18, zoomOffset: 0 })
      }
    },
    handleResize() {
      if (this.map) {
        this.map.invalidateSize()
      }
    },
    goDeviceList() {
      this.$router.push('/device/list')
    }
  }
}
</script>

<style scoped lang="scss">
.map-shell >>> .gas-page-body {
  padding: 0;
  background: transparent;
  box-shadow: none;
}

.map-page {
  display: flex;
  gap: 0;
  min-height: calc(100vh - 168px);
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.map-main {
  flex: 1;
  min-width: 0;
  position: relative;
  background: #eef1f6;
}

.map-chart {
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 168px);
  z-index: 1;
}

.map-hint {
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 500;
  max-width: calc(100% - 24px);
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #4e5969;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e5e6eb;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  pointer-events: none;
}

.map-mode-switch {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 500;
  display: inline-flex;
  padding: 3px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #d9dee8;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.map-mode-switch button {
  min-width: 48px;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #4e5969;
  font-size: 12px;
  line-height: 28px;
  cursor: pointer;
}
.map-mode-switch button.active {
  background: #1677ff;
  color: #fff;
  font-weight: 600;
}
.map-mode-switch button:not(.active):hover {
  background: #f2f3f5;
  color: #1d2129;
}

.map-sidebar {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e5e6eb;
  background: #fff;
}

.sidebar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #f2f3f5;
}
.sidebar-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.sidebar-stats {
  display: flex;
  gap: 10px;
  font-size: 12px;
}
.sidebar-stats .st {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #4e5969;
}
.sidebar-stats .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.st.online .dot { background: #52c41a; }
.st.alarm .dot { background: #ff4d4f; }
.st.offline .dot { background: #bfbfbf; }

.sidebar-search {
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  align-items: center;
}
.sidebar-search .el-input {
  flex: 1;
}

.device-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;
}

.device-card {
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  background: #fff;
}
.device-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.device-card.active {
  border-color: #1677ff;
  box-shadow: 0 0 0 1px rgba(22, 119, 255, 0.2);
}
.device-card.alarm {
  background: #fff7f7;
  border-color: #ffccc7;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.status-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
}
.status-tag.online {
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}
.status-tag.offline {
  color: #86909c;
  background: #f7f8fa;
  border: 1px solid #e5e6eb;
}
.status-tag.alarm {
  color: #cf1322;
  background: #fff1f0;
  border: 1px solid #ffa39e;
}
.card-loc {
  color: #c9cdd4;
  font-size: 16px;
}
.card-loc.precise {
  color: #1677ff;
}

.card-name {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
  line-height: 1.4;
}
.card-code {
  font-size: 12px;
  color: #86909c;
  margin-top: 2px;
}
.card-site {
  font-size: 12px;
  color: #4e5969;
  margin-top: 6px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-alarm-msg {
  margin-top: 8px;
  font-size: 12px;
  color: #f53f3f;
  line-height: 1.4;
}

.empty-tip {
  text-align: center;
  color: #86909c;
  padding: 24px 0;
  font-size: 13px;
}

.sidebar-foot {
  padding: 12px 16px;
  border-top: 1px solid #f2f3f5;
  text-align: center;
  color: #1677ff;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}
.sidebar-foot:hover {
  background: #f7f8fa;
}

@media (max-width: 1100px) {
  .map-page {
    flex-direction: column;
  }
  .map-sidebar {
    width: 100%;
    max-height: 360px;
  }
  .map-chart {
    min-height: 420px;
  }
  .map-mode-switch {
    top: 10px;
    left: 10px;
  }
}
</style>

<style lang="scss">
.device-map-marker-wrap {
  background: transparent;
  border: none;
}
.device-map-marker {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
.device-map-marker.alarm {
  animation: map-pulse 1.2s ease-in-out infinite;
}
@keyframes map-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.25); }
}

.leaflet-popup-content-wrapper {
  border-radius: 8px;
}
.leaflet-popup-close-button {
  top: 8px !important;
  right: 8px !important;
  width: 20px !important;
  height: 20px !important;
  line-height: 18px !important;
  border-radius: 50%;
  color: #86909c !important;
  font-size: 18px !important;
}
.leaflet-popup-close-button:hover {
  background: #f2f3f5;
  color: #1d2129 !important;
}
.map-popup {
  min-width: 280px;
  max-width: 340px;
  padding-right: 18px;
}
.map-popup-title {
  font-weight: 600;
  font-size: 14px;
  color: #1d2129;
}
.map-popup-code {
  font-size: 12px;
  color: #86909c;
  margin-top: 2px;
}
.map-popup-row {
  margin-top: 6px;
  font-size: 12px;
  color: #4e5969;
}
.map-popup-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin-top: 8px;
}
.map-popup-metric {
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid #edf0f5;
  border-radius: 4px;
  background: #f7f8fa;
}
.map-popup-metric span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 16px;
  color: #86909c;
}
.map-popup-metric strong {
  display: block;
  overflow-wrap: anywhere;
  margin-top: 2px;
  font-size: 13px;
  line-height: 18px;
  color: #1d2129;
  font-weight: 600;
}
.map-popup-metric small {
  margin-left: 3px;
  font-size: 10px;
  color: #86909c;
  font-weight: 400;
}
.map-popup-site {
  margin-top: 6px;
  font-size: 12px;
  color: #1d2129;
  line-height: 1.45;
}
.map-popup-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.map-popup-tab {
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #4e5969;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 20px;
  cursor: pointer;
}
.map-popup-tab.active {
  border-color: #1677ff;
  background: #e6f4ff;
  color: #1677ff;
}
.map-popup-sub-panel[hidden] {
  display: none;
}
.map-popup-hint {
  margin-top: 6px;
  font-size: 11px;
  color: #fa8c16;
}
.map-popup-alarm {
  margin-top: 6px;
  font-size: 12px;
  color: #f53f3f;
  line-height: 1.4;
}
</style>
